import { useAtom } from 'jotai';
import { MapPin } from 'lucide-react';
import SeverityBadge from './SeverityBadge';
import ConfidenceBadge from './ConfidenceBadge';
import { selectedFindingIdAtom } from '@/hooks/atoms';
import { SEVERITY_COLORS, type Finding } from '@/types/forensics';

interface FindingListItemProps {
  finding: Finding;
}

export default function FindingListItem({ finding }: FindingListItemProps) {
  const [selectedId, setSelectedId] = useAtom(selectedFindingIdAtom);
  const isSelected = selectedId === finding.id;

  return (
    <button
      onClick={() => setSelectedId(isSelected ? null : finding.id)}
      className={`
        w-full text-left p-3 rounded-md border transition-all
        ${isSelected ? 'ring-2 shadow-sm' : 'hover:bg-muted/50'}
      `}
      style={isSelected ? { borderColor: SEVERITY_COLORS[finding.severity] } : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-tight">{finding.title}</p>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{finding.shortExplanation}</p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <SeverityBadge severity={finding.severity} />
          <ConfidenceBadge confidence={finding.confidence} />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2">
        {finding.region && (
          <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" />
            Localized
          </span>
        )}
        {finding.evidence.map((e, i) => (
          <span key={i} className="text-xs bg-muted px-1.5 py-0.5 rounded">
            {e.moduleName.replace(/_/g, ' ')}
          </span>
        ))}
      </div>
    </button>
  );
}
